'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface ToggleOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

interface ToggleSwitchProps<T extends string> {
  options: ToggleOption<T>[];
  activeOption: T;
  onOptionClick: (option: T) => void;
  className?: string;
}

export function ToggleSwitch<T extends string>({
  options,
  activeOption,
  onOptionClick,
  className,
}: ToggleSwitchProps<T>) {
  return (
    <div
      role="tablist"
      aria-label="Product category toggle"
      className={cn(
        'relative flex w-full p-1',
        'flex-col space-y-1 rounded-xl bg-slate-200/70 dark:bg-slate-800/90',
        'md:flex-row md:space-y-0 md:space-x-1 md:rounded-full',
        className
      )}
    >
      {options.map(option => (
        <button
          key={option.value}
          role="tab"
          aria-selected={activeOption === option.value}
          onClick={() => onOptionClick(option.value)}
          className={cn(
            'relative flex-1 cursor-pointer items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 md:rounded-full',
            activeOption === option.value
              ? 'text-slate-900 dark:text-white'
              : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
          )}
        >
          {activeOption === option.value && (
            <motion.div
              layoutId="active-toggle-pill"
              className="absolute inset-0 z-0 rounded-lg bg-white shadow-md dark:bg-slate-900/60 md:rounded-full"
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
            />
          )}
          <span className="relative z-10 flex items-center justify-center gap-2 whitespace-nowrap">
            {option.icon}
            {option.label}
          </span>
        </button>
      ))}
    </div>
  );
}
